import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Row, Col, Card } from "reactstrap";
import ReactTable from "react-table";
import { connect } from "react-redux";
import fetchVariationChildService from "../../../../../Service/OPSServices/AmazonProductListing_v1/fetchVariationChildService";

// ── Strict Select Cell (custom dropdown — table बाहेर पण जातो) ──
function StrictSelectCell({ attr, initialVal, allowedValues, allowedLabels, asin, onAttrChange }) {
    const [localVal, setLocalVal] = useState(initialVal || "");
    const [isOpen, setIsOpen] = useState(false);
    const [dropUp, setDropUp] = useState(false);
    const wrapperRef = useRef(null);

    const prevRef = useRef(initialVal);
    useEffect(() => {
        if (prevRef.current !== initialVal) {
            prevRef.current = initialVal;
            setLocalVal(initialVal || "");
        }
    }, [initialVal]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const checkDropDirection = () => {
        if (wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            setDropUp(spaceBelow < 250);
        }
    };

    const selectedLabel = allowedValues.reduce((found, opt, i) => {
        if (String(opt) === String(localVal)) return (allowedLabels && allowedLabels[i]) || String(opt);
        return found;
    }, null);

    return (
        <div ref={wrapperRef} style={{ width: "100%", position: "relative" }}>
            <div
                onClick={() => {
                    checkDropDirection();
                    setIsOpen((prev) => !prev);
                }}
                style={{
                    fontSize: 12, borderRadius: 4, padding: "2px 24px 2px 6px",
                    width: "100%", border: "1px solid #ccc", background: "#fff",
                    color: localVal ? "#111" : "#999", cursor: "pointer",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    userSelect: "none", minHeight: 24,
                }}
            >
                <span>{selectedLabel || localVal || "-- select --"}</span>
                <span style={{ fontSize: 10, color: "#666" }}>▼</span>
            </div>

            {isOpen && (
                <div style={{
                    position: "fixed",
                    background: "#fff", border: "1px solid #d5d9d9",
                    borderRadius: 8, maxHeight: 220,
                    overflowY: "auto", zIndex: 99999,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    minWidth: 150,
                    ...(dropUp
                        ? {
                            bottom: wrapperRef.current
                                ? window.innerHeight - wrapperRef.current.getBoundingClientRect().top + 2
                                : "auto",
                            left: wrapperRef.current
                                ? wrapperRef.current.getBoundingClientRect().left
                                : 0,
                        }
                        : {
                            top: wrapperRef.current
                                ? wrapperRef.current.getBoundingClientRect().bottom + 2
                                : "auto",
                            left: wrapperRef.current
                                ? wrapperRef.current.getBoundingClientRect().left
                                : 0,
                        }
                    ),
                }}>
                    <div
                        onMouseDown={() => {
                            setLocalVal("");
                            onAttrChange(asin, attr, "");
                            setIsOpen(false);
                        }}
                        style={{
                            padding: "8px 12px", cursor: "pointer", fontSize: 12,
                            color: "#999", background: localVal === "" ? "#e8f4fd" : "#fff"
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f3f3"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = localVal === "" ? "#e8f4fd" : "#fff"; }}
                    >
                        -- select --
                    </div>
                    {allowedValues.map((opt, i) => {
                        const label = (allowedLabels && allowedLabels[i]) || String(opt);
                        const isSelected = String(opt) === String(localVal);
                        return (
                            <div
                                key={String(opt)}
                                onMouseDown={() => {
                                    setLocalVal(String(opt));
                                    onAttrChange(asin, attr, String(opt));
                                    setIsOpen(false);
                                }}
                                style={{
                                    padding: "8px 12px", cursor: "pointer", fontSize: 12,
                                    background: isSelected ? "#e8f4fd" : "#fff",
                                    color: "#111", fontWeight: isSelected ? 600 : 400,
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f3f3"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? "#e8f4fd" : "#fff"; }}
                            >
                                {label}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── Suggestive Attr Input ──────────────────────────────────
function SuggestiveAttrInput({ attr, initialVal, allowedValues, asin, onAttrChange }) {
    const [localVal, setLocalVal] = useState(initialVal || "");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [dropUp, setDropUp] = useState(false);
    const inputRef = useRef(null);

    const prevInitialRef = useRef(initialVal);
    useEffect(() => {
        if (prevInitialRef.current !== initialVal) {
            prevInitialRef.current = initialVal;
            setLocalVal(initialVal || "");
        }
    }, [initialVal]);

    const checkDropDirection = () => {
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            setDropUp(spaceBelow < 250);
        }
    };

    const allOptions = localVal && !allowedValues.includes(localVal)
        ? [localVal, ...allowedValues]
        : allowedValues;

    const filteredOptions = allOptions.filter(opt =>
        String(opt).toLowerCase().includes(String(localVal || "").toLowerCase())
    );

    return (
        <div style={{ width: "100%", position: "relative" }}>
            <input
                ref={inputRef}
                style={{ fontSize: 12, borderRadius: 4, padding: "2px 6px", width: "100%", border: "1px solid #ccc", background: "#fff", color: "#111" }}
                value={localVal}
                onFocus={() => {
                    checkDropDirection();
                    setShowSuggestions(true);
                }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onChange={(e) => {
                    setLocalVal(e.target.value);
                    onAttrChange(asin, attr, e.target.value);
                    setShowSuggestions(true);
                }}
                placeholder={`Search ${attr}...`}
                autoComplete="off"
            />
            {showSuggestions && filteredOptions.length > 0 && (
                <div style={{
                    position: "fixed",
                    ...(dropUp
                        ? {
                            bottom: inputRef.current
                                ? window.innerHeight - inputRef.current.getBoundingClientRect().top + 2
                                : "auto",
                            left: inputRef.current ? inputRef.current.getBoundingClientRect().left : 0,
                        }
                        : {
                            top: inputRef.current ? inputRef.current.getBoundingClientRect().bottom + 2 : "auto",
                            left: inputRef.current ? inputRef.current.getBoundingClientRect().left : 0,
                        }
                    ),
                    width: inputRef.current ? inputRef.current.getBoundingClientRect().width : 150,
                    background: "#fff", border: "1px solid #d5d9d9",
                    borderRadius: 8, maxHeight: 220,
                    overflowY: "auto", zIndex: 99999,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)"
                }}>
                    {filteredOptions.slice(0, 50).map((opt) => (
                        <div
                            key={String(opt)}
                            onMouseDown={() => {
                                setLocalVal(String(opt));
                                onAttrChange(asin, attr, String(opt));
                                setShowSuggestions(false);
                            }}
                            style={{ padding: "10px 12px", cursor: "pointer", fontSize: 12, background: "#fff", color: "#111" }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f3f3"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
                        >
                            {String(opt)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Plain Editable Cell ────────────────────────────────────
function EditableCell({ attr, initialVal, asin, onAttrChange }) {
    const [localVal, setLocalVal] = useState(initialVal || "");

    const prevInitialRef = useRef(initialVal);
    useEffect(() => {
        if (prevInitialRef.current !== initialVal) {
            prevInitialRef.current = initialVal;
            setLocalVal(initialVal || "");
        }
    }, [initialVal]);

    return (
        <input
            type="text"
            value={localVal}
            onChange={(e) => {
                setLocalVal(e.target.value);
                onAttrChange(asin, attr, e.target.value);
            }}
            style={{
                fontSize: 12, borderRadius: 4, padding: "2px 6px",
                width: "100%", border: "1px solid #ccc"
            }}
            placeholder={`Enter ${attr}`}
        />
    );
}

// ── Main Component ─────────────────────────────────────────
function VariationsChildGrid(props) {
    const [variationData, setVariationData] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editedAttrs, setEditedAttrs] = useState({});

    useEffect(() => {
        if (!props.selectedAsin) return;
        setLoading(true);
        setError(null);
        setEditedAttrs({});

        const data = {
            asin: props.selectedAsin,
            sku: props.sku || "",
            variation_theme: props.prefetchedVariationTheme || null,
        };
        fetchVariationChildService(props.token, data).then((res) => {
            if (res && res.status) {
                setVariationData(res);
            } else {
                setError((res && res.message) || "Failed to load variation data.");
            }
            setLoading(false);
        });
    }, [props.selectedAsin, props.sku, props.prefetchedVariationTheme]);

    const handleAttrChange = useCallback((asin, attrKey, value) => {
        setEditedAttrs((prev) => ({
            ...prev,
            [asin]: { ...(prev[asin] || {}), [attrKey]: value },
        }));
    }, []);

    // Schema बघून field type ठरव
    const getAttrFieldMeta = useCallback((attrKey) => {
        if (!props.schemaFields) return { allowedValues: [], allowedLabels: [], isSuggestive: false, isStrictSelect: false };
        const field = props.schemaFields.find(
            (f) => f.field === attrKey || f.field === attrKey.toLowerCase()
        );
        if (!field) return { allowedValues: [], allowedLabels: [], isSuggestive: false, isStrictSelect: false };

        const options     = (field.schema && field.schema.allowed_values) || [];
        const allowCustom = field.schema && field.schema.rules && field.schema.rules.allow_custom_values;
        const isDropdown  = field.render && field.render.component === "SELECT";

        return {
            allowedValues:  options,
            allowedLabels:  (field.schema && field.schema.allowed_value_labels) || [],
            isSuggestive:   options.length > 0 && allowCustom === true,
            isStrictSelect: isDropdown && options.length > 0 && allowCustom !== true,
        };
    }, [props.schemaFields]);

    const children = ((variationData && variationData.children) || []).filter((c) => !c.is_parent);
    const varAttrs = (variationData && variationData.variation_attributes) || [];
    const theme    = (variationData && variationData.variation_theme) || "";

    const filteredChildren = children.filter((row) => {
        const text  = searchText.toLowerCase();
        const asin  = (row.asin || "").toLowerCase();
        const title = (row.title || "").toLowerCase();
        const skus  = (row.skus || []).join(" ").toLowerCase();
        const attrs = Object.values(row.attributes || {}).join(" ").toLowerCase();
        return asin.includes(text) || title.includes(text) || skus.includes(text) || attrs.includes(text);
    });

    const columns = useMemo(() => [
        {
            Header: "ASIN",
            accessor: "asin",
            width: 150,
            Cell: (cellInfo) => (
                <div className="d-flex justify-content-center w-100">
                    <p className="mb-0" style={{ fontFamily: "monospace", fontSize: 12 }}>
                        {cellInfo.value}
                    </p>
                </div>
            ),
        },
        {
            Header: "SKUs",
            accessor: "skus",
            width: 400,
            Cell: (cellInfo) => {
                const skus = cellInfo.value || [];
                return (
                    <div className="d-flex flex-wrap w-100" style={{ gap: 4 }}>
                        {skus.length === 0 ? (
                            <p className="mb-0 text-muted">—</p>
                        ) : (
                            skus.map((s, i) => (
                                <span key={i} style={{
                                    fontFamily: "monospace", fontSize: 11,
                                    background: "#f3f4f6", border: "1px solid #dde1e7",
                                    borderRadius: 4, padding: "1px 6px", color: "#374151",
                                }}>
                                    {s}
                                </span>
                            ))
                        )}
                    </div>
                );
            },
        },
        {
            Header: "Product Title",
            accessor: "title",
            Cell: (cellInfo) => (
                <div className="d-flex justify-content-start w-100">
                    <p className="mb-0">{cellInfo.value || "—"}</p>
                </div>
            ),
        },
        ...varAttrs.map((attr) => ({
            Header: attr.charAt(0).toUpperCase() + attr.slice(1),
            accessor: attr,
            width: 180,
            Cell: (cellInfo) => {
                const currentVal = (cellInfo.original.attributes && cellInfo.original.attributes[attr]) || "";
                const { allowedValues, allowedLabels, isSuggestive, isStrictSelect } = getAttrFieldMeta(attr);

                return (
                    <div className="d-flex justify-content-center w-100">
                        {isStrictSelect ? (
                            <StrictSelectCell
                                key={`${cellInfo.original.asin}-${attr}`}
                                attr={attr}
                                initialVal={currentVal}
                                allowedValues={allowedValues}
                                allowedLabels={allowedLabels}
                                asin={cellInfo.original.asin}
                                onAttrChange={handleAttrChange}
                            />
                        ) : isSuggestive ? (
                            <SuggestiveAttrInput
                                key={`${cellInfo.original.asin}-${attr}`}
                                attr={attr}
                                initialVal={currentVal}
                                allowedValues={allowedValues}
                                asin={cellInfo.original.asin}
                                onAttrChange={handleAttrChange}
                            />
                        ) : (
                            <EditableCell
                                key={`${cellInfo.original.asin}-${attr}`}
                                attr={attr}
                                initialVal={currentVal}
                                asin={cellInfo.original.asin}
                                onAttrChange={handleAttrChange}
                            />
                        )}
                    </div>
                );
            },
        })),
    ], [varAttrs, getAttrFieldMeta, handleAttrChange]);

    if (error) {
        return (
            <div className="mb-3 p-3" style={{
                background: "#fff3f3", border: "1px solid #ffb3b3",
                borderRadius: 8, color: "#cc0000", fontSize: 13,
            }}>
                {error}
            </div>
        );
    }

    return (
        <Card className="card-of-problemarea p-3 mb-4 mt-4 shadow-sm border-0">
            <Row className="mb-3 align-items-center">
                <Col md={7}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                        <h4 className="listing-page-title mb-0" style={{ fontWeight: "bold" }}>
                            Child Products
                        </h4>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#f0f4f8", border: "1px solid #d0d7de", borderRadius: 20, padding: "3px 12px", fontSize: 12, color: "#374151", fontWeight: 500 }}>
                            Theme :&nbsp;<b style={{ color: "#1a6bb5" }}>{theme}</b>
                        </span>
                        {variationData && variationData.parent_asin && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#f0f4f8", border: "1px solid #d0d7de", borderRadius: 20, padding: "3px 12px", fontSize: 12, color: "#374151", fontWeight: 500 }}>
                                Parent ASIN :&nbsp;
                                <b style={{ fontFamily: "monospace", color: "#1a6bb5" }}>{variationData.parent_asin}</b>
                            </span>
                        )}
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#f0f4f8", border: "1px solid #d0d7de", borderRadius: 20, padding: "3px 12px", fontSize: 12, color: "#374151", fontWeight: 500 }}>
                            Child :&nbsp;<b>{children.length}</b>
                        </span>
                    </div>
                </Col>
                <Col md={5} className="d-flex justify-content-end">
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        className="form-control w-50"
                    />
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <ReactTable
                        data={filteredChildren}
                        columns={columns}
                        loading={loading}
                        className="-highlight -striped"
                        getTheadProps={() => ({
                            style: { overflowY: "hidden", background: "#B1FA63", color: "#243837" },
                        })}
                        getTrProps={(state, rowInfo) => {
                            if (!rowInfo) return {};
                            return {
                                style: {
                                    background: rowInfo.original.is_parent ? "#fffbeb" : undefined,
                                    fontStyle: rowInfo.original.is_parent ? "italic" : "normal",
                                    overflow: "visible",
                                },
                            };
                        }}
                        defaultPageSize={5}
                        noDataText={loading ? "Loading..." : "No Data Found."}
                    />
                </Col>
            </Row>
        </Card>
    );
}

const mapStateToProps = (state) => ({
    token: state.loggedUser.token,
});

export default connect(mapStateToProps)(VariationsChildGrid);